import {BRAND_APP,isBrandAppPath} from '@/lib/appRealm';
import ClientAccessGate from "@/components/ClientAccessGate";
import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr } from "@/i18n/copy";
import Support from "@/pages/Support";

import ProfessionalMaterials from "@/pages/ProfessionalMaterials";
import CampaignLanding from "@/pages/CampaignLanding";
import AgentWorkspace from "@/pages/AgentWorkspace";
import AgentLayout from "@/components/AgentLayout";
import {isAgentRole} from "@/agentWorkspace";
import GiftLanding from "@/pages/GiftLanding";
import {CustomerTagProvider} from "@/components/CustomerTag";
import {brandAuthPath,brandInvitationDestination} from "@/brandInvitation";
import TpsAccessSplash from "@/components/TpsAccessSplash";
import "@/App.css";
import { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Layout from "@/components/Layout";
import deferredPage from "@/components/DeferredPage";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import PasswordRecovery from "@/pages/PasswordRecovery";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import TeamInvitationAccept from "@/pages/TeamInvitationAccept";
import ClientInvitationAccept from "@/pages/ClientInvitationAccept";

import Projects from "@/pages/Projects";
import ProjectsEntry from "@/pages/ProjectsEntry";
import Calendar from "@/pages/Calendar";
import ConsentArchive from "@/pages/ConsentArchive";
import Portfolio from "@/pages/Portfolio";
import Quotations from "@/pages/Quotations";
import Team from "@/pages/Team";
import GuestWorkspace from "@/pages/GuestWorkspace";
import {GuestDetail} from "@/components/GuestCards";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import Audit from "@/pages/Audit";
import PaymentResult from "@/pages/PaymentResult";
import FeatureCenter from "@/pages/FeatureCenter";
import Scanner from "@/pages/Scanner";
import ModuleLocked from "@/pages/ModuleLocked";
import ArtistProfile from "@/pages/ArtistProfile";
import ArtistServices from "@/pages/ArtistServices";
import {hasTattooActivity, serviceJourneyUrl} from "@/serviceTypes";
import ArtistPortfolio from "@/pages/ArtistPortfolio";
import ArtistCompliance from "@/pages/ArtistCompliance";

import FinanceAccess from "@/components/FinanceAccess";
import ArtistFinance from "@/pages/ArtistFinance";
import ArtistTools from "@/pages/ArtistTools";
import OperationsWorkspace from "@/pages/OperationsWorkspace";
import { MANAGER_MODULES, FRONT_DESK_MODULES } from "@/roleWorkspace";
import { applicationPathForRole, workspacePathForRole } from "@/pwa";
import ClientRoute from "@/pages/ClientRoute";
import StudioSetupWizard from "@/pages/StudioSetupWizard";
import OwnerLanding from "@/components/OwnerLanding";
import { OwnerTermsGate } from "@/components/CommercialTerms";
import Landing from "@/pages/Landing";
import SubscriptionBilling from "@/pages/SubscriptionBilling";
const BrandHQ = deferredPage(() => import("@/pages/BrandHQ"));
import BrandAppEntry from "@/pages/BrandAppEntry";
import BrandLaunchRestore from "@/components/BrandLaunchRestore";
import { shouldGuideOwnerToSetup } from "@/studioSetup";
import { api } from "@/lib/api";
import SetupErrorBoundary from "@/components/SetupErrorBoundary";
const ClientAppSettings = deferredPage(() => import("@/pages/ClientAppSettings"));
import { OwnerProfessionalScope } from "@/context/AuthContext";
import Activation from "@/pages/Activation";
import ActivationGate from "@/components/ActivationGate";
import PublicStudio from "@/pages/PublicStudio";
import PublicClientRegister from "@/pages/PublicClientRegister";
import ClientStudioLogin from "@/pages/ClientStudioLogin";
import Presence from "@/pages/Presence";

// Keep charting and platform administration out of login and client journeys. The route
// wrappers below stay synchronous and retain their existing behavior.
// Load large contact directories when opened, preserving the shared recovery boundary.
const Customers = deferredPage(() => import('@/pages/Customers'));
const ArtistClients = deferredPage(() => import('@/pages/ArtistClients'));
const StudioOffboarding = deferredPage(() => import('@/components/StudioOffboarding'));
const Admin = deferredPage(() => import("@/pages/Admin"));
const OwnerWorkspace = deferredPage(() => import("@/pages/OwnerWorkspace"));
const ArtistWorkspace = deferredPage(() => import("@/pages/ArtistWorkspace"));
const DashboardPro = deferredPage(() => import("@/pages/DashboardPro"));
const WalkInStudio = deferredPage(() => import("@/pages/WalkInChat"));
const BrandArea = deferredPage(() => import("@/pages/BrandArea"));
const BrandCustody = deferredPage(() => import("@/pages/BrandCustody"));
const Warehouse = deferredPage(() => import("@/pages/Warehouse"));
const Supply = deferredPage(() => import("@/pages/Supply"));
const ShopFront = deferredPage(() => import("@/pages/ShopFront"));
const ShopManagement = deferredPage(() => import("@/pages/ShopManagement"));
const Reports = deferredPage(() => import("@/pages/Reports"));
const Documents = deferredPage(() => import("@/pages/Documents"));
const Finance = deferredPage(() => import("@/pages/Finance"));
const MarketingStudio = deferredPage(() => import("@/pages/MarketingStudio"));
const Growth = deferredPage(() => import("@/pages/Growth"));

function Splash({ professional = true }) {
  useUiLanguage();
  const { bootstrapError, loadMe } = useAuth();
  const [retrying, setRetrying] = useState(false);
  if (bootstrapError) return <main className="grid min-h-screen place-items-center p-6"><section role="alert" className="max-w-md space-y-4 text-center"><p>{tr("Non siamo riusciti a caricare il tuo spazio di lavoro. Riprova.")}</p><button className="min-h-11 rounded-lg border px-5" disabled={retrying} onClick={async () => { setRetrying(true); try { await loadMe(); } catch {} finally { setRetrying(false); } }}>{tr(retrying ? "Caricamento…" : "Riprova")}</button></section></main>;
  if (professional) return <TpsAccessSplash />; return <div className="min-h-screen grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>; }
const Organizations = deferredPage(() => import("@/pages/Organizations"));
const BrandLogin = deferredPage(() => import('@/pages/BrandLogin'));
const BrandHandoverSetup = deferredPage(() => import('@/pages/BrandHandoverSetup'));
const HeadquartersSetup = deferredPage(() => import('@/pages/HeadquartersSetup'));
const BrandRecruitment = deferredPage(() => import('@/pages/BrandRecruitment'));
const BrandConsole = deferredPage(() => import("@/pages/BrandConsole"));

function Protected({ children }) {
  useUiLanguage(); const { status, user } = useAuth(); if (status === "loading") return <Splash />; if (status === "guest") return <Navigate to="/login" replace />; if (user?.role === "organization_user") return <Navigate to="/brand-console/login" replace />; if (user?.role === "client") return <Navigate to={applicationPathForRole(user.role)} replace />; if (user?.role === "platform_admin") return <Navigate to="/admin" replace />; if (user?.role === "studio_owner" && user?.terms_acceptance_required) return <OwnerTermsGate />; return isAgentRole(user?.role) ? <AgentLayout><ActivationGate>{children}</ActivationGate></AgentLayout> : <Layout><ActivationGate>{children}</ActivationGate></Layout>; }
function BrandProtected({children}) {
  useUiLanguage();const {status,user}=useAuth();const loc=useLocation();if(status==="loading")return <Splash/>;if(status==="guest")return <Navigate to={brandAuthPath("login",loc.search)} replace/>;if(user?.role==="platform_admin")return children;if(user?.role!=="studio_owner")return <Navigate to="/app" replace/>;if(user?.terms_acceptance_required)return <OwnerTermsGate/>;return <Layout>{children}</Layout>;}
function ArtistOnly({ children, tattooOnly = false }) {
  useUiLanguage(); const { status, user, workspace } = useAuth(); if (status === "loading") return <Splash />; if (status === "guest") return <Navigate to="/login" replace />; if ((workspace?.role || user?.role) !== "artist" && !(user?.role === "studio_owner" && workspace?.owner_professional)) return <Navigate to="/app" replace />; if(tattooOnly && !hasTattooActivity(user?.role === "studio_owner" ? workspace?.owner_professional : workspace))return <Navigate to="/app/artist" replace />; return <OwnerProfessionalScope><Layout><ActivationGate>{children}</ActivationGate></Layout></OwnerProfessionalScope>; }
function PlatformProtected({ children }) {
  useUiLanguage(); const { status, user } = useAuth(); if (status === "loading") return <Splash />; if (status === "guest") return <Navigate to="/login" replace />; if (user?.role !== "platform_admin") return <Navigate to="/app" replace />; return children; }
const CommercialTermsPage = deferredPage(() => import('@/components/PublicLegalPage'));
const LeadForm = lazy(() => import('@/pages/LeadForm'));

function StudioClientRedirect() {
  const {studioSlug} = useParams(); const location = useLocation();
  return <Navigate to={serviceJourneyUrl(studioSlug, "client", location.search)} replace />;
}

function ClientProtected({ children }) {
  return <ClientAccessGate loading={<Splash professional={false} />}>{children}</ClientAccessGate>;
}
function Gate({ module, children }) {
  useUiLanguage(); const { hasModule } = useAuth(); if (module && !hasModule(module)) return <ModuleLocked module={module} />; return children; }
function Public({ children }) {
  useUiLanguage(); const { status, user } = useAuth(); const loc=useLocation(); if (status === "loading") return <Splash />; if (status === "authed" && loc.pathname === "/login" && new URLSearchParams(loc.search).get("studio_id")) return children; if (status === "authed" && loc.pathname === "/register" && new URLSearchParams(loc.search).get("new-studio") === "1" && ["studio_owner","artist","front_desk","manager","agent_marketing"].includes(user?.role)) return children; if (status === "authed" && (new URLSearchParams(loc.search).get("organizations") === "1" || user?.role === "organization_user")) return <Navigate to="/brand-console/login" replace />; if (status === "authed") return <Navigate to={user?.role==="studio_owner" ? brandInvitationDestination(loc.search)||"/app" : "/app"} replace />; return children; }
function WorkspaceRedirect() {
  useUiLanguage(); const { status, user, workspace } = useAuth(); if (status === "loading") return <Splash />; if (status === "guest") return <Navigate to="/login" replace />; if (user?.role === "organization_user") return <Navigate to="/brand-console/login" replace />; if (user?.role === "client") return <Navigate to={applicationPathForRole(user.role)} replace />; if (user?.role === "platform_admin") return <Navigate to="/admin" replace />; if ((workspace?.role || user?.role) === "studio_owner") return <OwnerLanding />; return <Navigate to={workspacePathForRole(workspace?.role || user?.role) || "/dashboard"} replace />; }
function TenantDashboardEntry() {
  useUiLanguage(); const { user } = useAuth(); if (user?.role === "platform_admin") return <Navigate to="/admin" replace />; return <DashboardPro />; }
function OwnerWorkspaceEntry() {
  useUiLanguage(); const { workspace } = useAuth(); const [setup, setSetup] = useState(null); useEffect(() => { if (workspace?.role === "studio_owner") api.get("/studio/setup").then(setSetup).catch(() => setSetup({ onboarding: { legacy: true } })); }, [workspace]); if (workspace?.role !== "studio_owner") return <Navigate to="/app" replace />; if (!setup) return <Splash />; if (shouldGuideOwnerToSetup(setup)) return <Navigate to="/setup" replace />; return <OwnerWorkspace />; }

function ClientRouteScrollReset() {
  const { pathname, hash } = useLocation();
  useLayoutEffect(() => {
    if (!hash && /^\/(client|studio|gift|campaign)(\/|$)/.test(pathname)) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [pathname, hash]);
  return null;
}

function AppRoutes() {
  useUiLanguage();
  return <Routes>
    <Route path="/legal" element={<CommercialTermsPage />} />
    <Route path="/organizations" element={<Navigate to="/app" replace />} />
    <Route path="/brand-console/login" element={<BrandLogin />} />
    <Route path="/brand-login" element={<Navigate to="/brand-console/login" replace />} />
    <Route path="/brand-register" element={<Navigate to="/brand-console/register" replace />} />
    <Route path="/brand-console/register" element={<Register />} />
    <Route path="/brand-handover" element={<BrandHandoverSetup />} />
    <Route path="/headquarters" element={<HeadquartersSetup />} />
    <Route path="/brand-console/:brandId/*" element={<BrandConsole />} />
    <Route path="/login" element={<Public><Login /></Public>} />
    <Route path="/forgot-password" element={<PasswordRecovery key="request" />} />
    <Route path="/reset-password" element={<PasswordRecovery key="reset" />} />
    <Route path="/register" element={<Public><Register /></Public>} />
    <Route path="/auth/callback" element={<AuthCallback />} />
    <Route path="/invite/team" element={<TeamInvitationAccept />} />
    <Route path="/invite/client" element={<ClientInvitationAccept />} />
    <Route path="/client" element={<ClientProtected><ClientRoute /></ClientProtected>} />
    <Route path="/shop" element={<ClientProtected><ShopFront /></ClientProtected>} />
    <Route path="/shop/:sellerId" element={<ShopFront />} />
    <Route path="/lead/:id" element={<Suspense fallback={<TpsAccessSplash/>}><LeadForm/></Suspense>} />
    <Route path="/campaign/:id" element={<CampaignLanding/>} />
    <Route path="/gift/:code" element={<GiftLanding />} />
    <Route path="/studio/:studioSlug" element={<PublicStudio />} />
    <Route path="/studio/:studioSlug/register" element={<PublicClientRegister />} />
    <Route path="/studio/:studioSlug/client" element={<StudioClientRedirect />} />
    <Route path="/studio/:studioSlug/login" element={<ClientStudioLogin />} />
    <Route path="/" element={<Public><Landing /></Public>} />
    <Route path="/app" element={<Protected><WorkspaceRedirect /></Protected>} />
    <Route path="/app/owner" element={<Protected><OwnerWorkspaceEntry /></Protected>} />
    <Route path="/activation" element={<Protected><Activation /></Protected>} />
    <Route path="/setup" element={<Protected><SetupErrorBoundary><StudioSetupWizard /></SetupErrorBoundary></Protected>} />
    <Route path="/support" element={<Protected><Support /></Protected>} />
    <Route path="/admin/support" element={<PlatformProtected><Support /></PlatformProtected>} />
    <Route path="/billing" element={<Protected><SubscriptionBilling /></Protected>} />
    <Route path="/app/manager" element={<Navigate to="/agent/manager" replace />} />
    <Route path="/agent" element={<Protected><AgentWorkspace /></Protected>} />
    <Route path="/agent/:view" element={<Protected><AgentWorkspace /></Protected>} />
    <Route path="/walkin" element={<Protected><WalkInStudio /></Protected>} />
    <Route path="/marketing" element={<Protected><MarketingStudio /></Protected>} />
    <Route path="/app/artist" element={<ArtistOnly><ArtistWorkspace /></ArtistOnly>} />
    <Route path="/app/artist/profile" element={<ArtistOnly><ArtistProfile /></ArtistOnly>} />
    <Route path="/app/artist/portfolio" element={<ArtistOnly tattooOnly><ArtistPortfolio /></ArtistOnly>} />
    <Route path="/app/artist/compliance" element={<ArtistOnly><ArtistCompliance /></ArtistOnly>} />
    <Route path="/app/artist/clients" element={<ArtistOnly><ArtistClients /></ArtistOnly>} />
    <Route path="/app/artist/services" element={<ArtistOnly><ArtistServices /></ArtistOnly>} />
    <Route path="/app/artist/finance" element={<ArtistOnly><ArtistFinance /></ArtistOnly>} />
    <Route path="/app/artist/materials" element={<ArtistOnly><ProfessionalMaterials /></ArtistOnly>} />
    <Route path="/consent/materials" element={<Protected><Gate module="consent"><ProfessionalMaterials /></Gate></Protected>} />
    <Route path="/app/artist/tools" element={<ArtistOnly><ArtistTools /></ArtistOnly>} />
    <Route path="/app/front-desk" element={<Protected><OperationsWorkspace role="front_desk" title={tr("Front Desk")} registry={FRONT_DESK_MODULES} includeConsents /></Protected>} />
    <Route path="/dashboard" element={<Protected><TenantDashboardEntry /></Protected>} />
    <Route path="/customers" element={<Protected><Gate module="crm"><Customers /></Gate></Protected>} />
    <Route path="/projects" element={<Protected><Gate module="projects"><ProjectsEntry /></Gate></Protected>} />
    <Route path="/calendar" element={<Protected><Gate module="scheduling"><Calendar /></Gate></Protected>} />
    <Route path="/artists" element={<Protected><Navigate to="/team" replace /></Protected>} />
    <Route path="/consent" element={<Protected><Gate module="consent"><ConsentArchive /></Gate></Protected>} />
    <Route path="/documents" element={<Protected><Gate module="documents"><Documents /></Gate></Protected>} />
    <Route path="/portfolio" element={<Protected><Gate module="portfolio"><Portfolio /></Gate></Protected>} />
    <Route path="/quotations" element={<Protected><Gate module="quotations"><Quotations /></Gate></Protected>} />
    <Route path="/warehouse" element={<Protected><Gate module="inventory"><Warehouse /></Gate></Protected>} />
    <Route path="/supply" element={<Protected><Gate module="inventory"><Supply /></Gate></Protected>} />
    <Route path="/shop-management" element={<Protected><Gate module="inventory"><ShopManagement /></Gate></Protected>} />
    <Route path="/finance" element={<Protected><FinanceAccess><Gate module="inventory"><Finance /></Gate></FinanceAccess></Protected>} />
    <Route path="/growth" element={<Protected><Gate module="loyalty"><Growth /></Gate></Protected>} />
    <Route path="/reports" element={<Protected><Gate module="analytics"><Reports /></Gate></Protected>} />
    <Route path="/features" element={<Protected><FeatureCenter /></Protected>} />
    <Route path="/scanner" element={<Protected><Gate module="scheduling"><Scanner /></Gate></Protected>} />
    <Route path="/team" element={<Protected><Team /></Protected>} />
    <Route path="/guests" element={<Protected><GuestWorkspace /></Protected>} />
    <Route path="/app/artist/guests" element={<ArtistOnly><GuestWorkspace /></ArtistOnly>} />
    <Route path="/guest/:guestId" element={<GuestDetail />} />
    <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
    <Route path="/presence" element={<Protected><Presence /></Protected>} />
    <Route path="/scanner-qr" element={<Protected><Navigate to="/scanner" replace /></Protected>} />
    <Route path="/data-exit" element={<StudioOffboarding />} />
    <Route path="/settings" element={<Protected><Settings /></Protected>} />
    <Route path="/client-app" element={<Protected><ClientAppSettings /></Protected>} />
    <Route path="/audit" element={<Protected><Gate module="audit"><Audit /></Gate></Protected>} />
    <Route path="/admin" element={<PlatformProtected><Admin /></PlatformProtected>} />
    <Route path="/hq/area" element={<Protected><BrandArea /></Protected>} />
    <Route path="/hq/custody" element={<Protected><BrandCustody /></Protected>} />
    <Route path="/hq" element={<BrandProtected><BrandHQ /></BrandProtected>} />
    <Route path="/brand/:brandSlug/affiliati" element={<BrandRecruitment/>}/>
          <Route path="/brand/:brandSlug" element={<BrandAppEntry />} />
    <Route path="/payment/success" element={<Protected><PaymentResult /></Protected>} />
    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Routes>;
}

export default function App() {
  useUiLanguage(); return <ThemeProvider><AuthProvider><BrowserRouter><ClientRouteScrollReset /><BrandLaunchRestore><CustomerTagProvider><RealmRoutes /></CustomerTagProvider></BrandLaunchRestore><Toaster position="top-right" richColors /></BrowserRouter></AuthProvider></ThemeProvider>; }



function RealmRoutes(){const location=useLocation();if(isBrandAppPath(location.pathname)!==BRAND_APP){window.location.replace(location.pathname+location.search+location.hash);return <Splash/>;}return <AppRoutes/>;}
